import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Divider,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Chip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next";
import EmplacementTree from "../../../Common/Components/EmplacementTree2";

export default function EditUserModal({
  open,
  onClose,
  user,
  onSave,
  departments,
  userLang,
  userState,
}) {
  const { t } = useTranslation(["Employee", "Common"]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    ...user,
    selectedEmplacements: user?.selectedEmplacements || [],
    estInactif: user?.estInactif || false,
  });

  const [provinceTree, setProvinceTree] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [loadingUserEmplacements, setLoadingUserEmplacements] = useState(true);
  const [saving, setSaving] = useState(false);

  // Reset formData à chaque ouverture
  useEffect(() => {
    if (open && user) {
      setFormData({
        ...user,
        selectedEmplacements: user?.selectedEmplacements || [],
        estInactif: user?.estInactif || false,
      });
    }
  }, [user, open]);

  // Fetch Province Tree
  useEffect(() => {
    if (!userState?.token) return;

    const fetchProvinceTree = async () => {
      try {
        setLoadingTree(true);
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/Localization/GetProvinceTree`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch tree");
        setProvinceTree(await res.json());
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingTree(false);
      }
    };

    fetchProvinceTree();
  }, [userState?.token]);

  // Fetch User Emplacements
  useEffect(() => {
    if (!userState?.token || !user?.id) return;

    const fetchUserEmplacements = async () => {
      try {
        setLoadingUserEmplacements(true);
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/Localization/GetUserEmplacements/${user.id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch user emplacements");
        const apiIds = await res.json();
        setFormData((prev) => ({
          ...prev,
          selectedEmplacements: Array.from(
            new Set([...(prev.selectedEmplacements || []), ...apiIds])
          ),
        }));
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingUserEmplacements(false);
      }
    };

    fetchUserEmplacements();
  }, [user?.id, userState?.token]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!userState?.token || !user?.noEmploye) return;

    const updatedUser = {
      ...formData,
      noEmploye: user.noEmploye,
    };

    try {
      setSaving(true);

      const payload = {
        noEmploye: user.noEmploye,
        estInactif: formData.estInactif || false,
        username: formData.username,
        firstname: formData.firstname,
        lastName: formData.lastName,
        email: formData.email,
        noCellulaire: formData.noTelephone,
        posPwd: formData.posPwd,
        title: formData.title,
        department: formData.department,
        language: formData.lang,
        idEmplacements: formData.selectedEmplacements,
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Employee/UpdateInternalEmploye/${user.noEmploye}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error(await res.text());

      // ✅ Update local grid seulement APRÈS succès
      onSave(updatedUser);
      onClose();
    } catch (error) {
      console.error("Update failed:", error);
      alert("La mise à jour a échoué.");
    } finally {
      setSaving(false);
    }
  };

  if (!formData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          height: isMobile ? "100%" : "85vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* TITLE */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EditIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Modifier un utilisateur
          </Typography>
          <Chip label="ÉDITION" size="small" color="primary" variant="outlined" />
        </Box>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Informations, département et emplacements autorisés
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ flex: 1, overflow: "hidden" }}>
        <Grid container spacing={3} sx={{ height: "100%" }}>
          {/* INFORMATIONS UTILISATEUR */}
          <Grid item xs={12} md={7} sx={{ height: "100%" }}>
            <Paper
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: 2,
                borderRadius: 2,
              }}
              variant="outlined"
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <InfoOutlinedIcon color="action" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Informations Utilisateur
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary" mb={1}>
                Modifier les informations de l'utilisateur
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Username"
                  value={formData.username || ""}
                  onChange={(e) => handleChange("username", e.target.value)}
                />
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstname || ""}
                  onChange={(e) => handleChange("firstname", e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName || ""}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email || ""}
                  onChange={(e) => handleChange("email", e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Telephone"
                  value={formData.noTelephone || ""}
                  onChange={(e) => handleChange("noTelephone", e.target.value)}
                />
                <TextField
                  fullWidth
                  label={t("POSpwd")}
                  value={formData.posPwd || ""}
                  onChange={(e) => handleChange("posPwd", e.target.value)}
                  inputProps={{ maxLength: 5 }}
                  error={formData.posPwd?.length > 5}
                  helperText={
                    formData.posPwd?.length > 5 ? t("Maximum 5 digits allowed") : ""
                  }
                />
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title || ""}
                  onChange={(e) => handleChange("title", e.target.value)}
                />
                <TextField
                  select
                  fullWidth
                  label="Department"
                  value={formData.department?.id || ""}
                  onChange={(e) =>
                    handleChange(
                      "department",
                      departments.find((d) => d.id === e.target.value)
                    )
                  }
                >
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {userLang === "FR" ? d.textFr : d.textEn}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Language"
                  value={formData.lang || "FR"}
                  onChange={(e) => handleChange("lang", e.target.value)}
                >
                  <MenuItem value="FR">Français</MenuItem>
                  <MenuItem value="EN">Anglais</MenuItem>
                </TextField>

                {/* Checkbox pour Utilisateur Inactif */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.estInactif || false}
                      onChange={(e) => handleChange("estInactif", e.target.checked)}
                    />
                  }
                  label="Utilisateur inactif"
                  sx={{ mt: 1 }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* EMPLACEMENTS */}
          <Grid item xs={12} md={5} sx={{ height: "100%" }}>
            <Paper
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: 2,
                borderRadius: 2,
              }}
              variant="outlined"
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <PlaceOutlinedIcon color="action" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Emplacements autorisés
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary" mb={1}>
                Modifier les emplacements accessibles
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {loadingTree || loadingUserEmplacements ? (
                  <Typography align="center" color="text.secondary">
                    Loading tree...
                  </Typography>
                ) : (
                  <EmplacementTree
                    data={provinceTree}
                    language={userLang === "FR" ? "fr" : "en"}
                    selectedIds={formData.selectedEmplacements}
                    onChange={(ids) =>
                      setFormData((prev) => ({
                        ...prev,
                        selectedEmplacements: ids,
                      }))
                    }
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      {/* FOOTER */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "space-between",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          label={`${formData.selectedEmplacements.length} emplacement(s) sélectionné(s)`}
        />

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}